<template>
  <div class="mb-4 text-white card bg-primary">
    <div class="card-body">
      <h4>Summary</h4>
      <hr />
      <dl>
        <template v-if="can('read_organizations')">
          <dt>Organization</dt>
          <dd>{{ customer.organization.name }}</dd>
        </template>

        <dt>Subscription</dt>
        <dd>
          <p v-if="customer.subscription">
            {{ customer.subscription.name }}
            <span class="ml-2 badge" :class="customerStatusBadgeClass">
              {{ customer.subscription.status | titlecase }}
            </span>
            <span
              class="ml-2 badge badge-dark"
              v-if="customer.quote"
              :class="customer.quote.is_forced ? 'badge-dark' : 'badge-dark'"
            >
              {{
                customer.quote.is_forced ? "has_force_quote" : "has_quote" | t
              }}
            </span>
          </p>
          <p v-else>{{ "none" | t }}</p>
        </dd>

        <dt>Quote</dt>
        <dd>
          <p v-if="customer.quote">{{ customer.quote.name }}</p>
          <p v-else>{{ "none" | t }}</p>
        </dd>

        <template v-if="latestPayment">
          <dt>Latest payment</dt>
          <dd>
            <template
              v-if="
                latestPayment.status === 'requires_auth' && isOnSecureConnection
              "
            >
              <span class="mr-2 badge badge-warning"
                >Requires authentication:</span
              >
              <button class="btn btn-secondary" @click="copyAuthorizationUrl">
                Copy link here
              </button>
            </template>
            <span v-else>{{ latestPayment.status }}</span>
          </dd>
        </template>

        <dt>Service status</dt>
        <dd>
          <span class="badge" :class="serviceStatusClass">{{
            serviceStatusText
          }}</span>
        </dd>

        <dt>Onboarding status</dt>
        <dd>
          <onboarding-status
            :status="customer.onboarding_status"
          ></onboarding-status>
        </dd>

        <dt>Payment method</dt>
        <dd>
          {{ customer.payment_method | t }}
          <span
            class="ml-2 badge badge-danger"
            v-if="
              customer.credit_card_count < 1 &&
              customer.payment_method === 'credit_card'
            "
          >
            {{ "missing_credit_card" | t }}
          </span>
        </dd>

        <template v-if="customer.subscription">
          <template v-if="customer.subscription.status === 'active'">
            <dt>Next renewal</dt>
            <dd>{{ customer.subscription.billing_period_end | date }}</dd>
          </template>
          <template v-if="customer.subscription.cancellation_created_at">
            <dt>Cancellation created at</dt>
            <dd>{{ customer.subscription.cancellation_created_at | date }}</dd>
          </template>
          <template v-if="customer.subscription.cancellation_date">
            <dt>Cancellation</dt>
            <dd>{{ customer.subscription.cancellation_date | date }}</dd>
          </template>
        </template>

        <dt>Created</dt>
        <dd>{{ customer.created_at | date }}</dd>

        <dt>Hubspot</dt>
        <dd v-if="customer.crm_reference">
          <u>
            <a
              class="text-white"
              :href="`https://app.hubspot.com/contacts/${hubspotPortalId}/company/${customer.crm_reference}/`"
              target="_blank"
            >
              {{ customer.crm_reference }}
            </a>
            <i class="pl-2 fa fa-external-link"></i>
          </u>
        </dd>
        <dd v-else><i>Missing Hubspot reference</i></dd>

        <hr />
        <button class="btn btn-secondary" @click="preview_invoice">
          Preview next invoice
        </button>
      </dl>
    </div>
  </div>
</template>

<script>
import Alerts from "@/utils/alerts.js";

export default {
  props: {
    customer: {
      type: Object,
      required: true,
    },
    hubspotPortalId: {
      type: String,
      required: true,
    },
  },
  computed: {
    customerStatusBadgeClass() {
      const statusClasses = {
        active: "badge-success",
        trial: "badge-warning",
        unbilled: "badge-dark",
        cancelled: "badge-danger",
      };
      return statusClasses[this.customer.subscription?.status] || "";
    },
    latestPayment() {
      return this.customer.payments?.[0];
    },
    isOnSecureConnection() {
      return window.location.protocol === "https:";
    },
    serviceStatusClass() {
      const statusClasses = {
        running: "badge-success",
        test: "badge-warning",
        disabled: "badge-danger",
        quote: "badge-dark",
        free_trial: "badge-dark",
      };
      return statusClasses[this.customer.service_status] || "";
    },
    serviceStatusText() {
      const statusTexts = {
        running: "Running",
        test: "Test",
        disabled: "Disabled",
        quote: "Quote",
        free_trial: "Free Trial",
      };
      return statusTexts[this.customer.service_status] || "";
    },
  },
  methods: {
    async copyAuthorizationUrl() {
      const authenticationUrl = this.latestPayment.authorization_url;
      try {
        await navigator.clipboard.writeText(authenticationUrl);
        Alerts.success({ title: "Url copied to clipboard!", detail: "" });
      } catch (error) {
        if (!this.isOnSecureConnection) {
          Alerts.danger({
            title: "Could not copy link..",
            detail: "Needs to be on a secure url (https:)",
          });
        }
      }
    },
    preview_invoice() {
      this.$emit("preview-invoice");
    },
  },
};
</script>